/**
 * Admin Resource Helpers
 * Helper functions for Content Manager API data transformation
 */

import { ContentManagerQueryOptions } from './content-manager.service';
import { API_CONFIG } from '../config/api.config';
import {
  transformDebitNoteFromStrapi,
  reverseTransformFieldNames,
} from './admin-resource-transforms';

// Re-export everything from admin-resource-transforms for backward compatibility
export {
  FIELD_MAPPINGS,
  transformFieldNames,
  reverseTransformFieldNames,
  transformDebitNoteFromStrapi,
  cleanDataForSubmit,
} from './admin-resource-transforms';

/**
 * Helper to get documentId from either the data object or id parameter
 * Strapi Content Manager API uses documentId for update/delete operations
 */
export const getDocumentId = (id: number | string, data?: any): string => {
  // If data has documentId, use it
  if (data?.documentId) {
    console.log('✅ [Admin Resources] Using documentId from data:', data.documentId);
    return data.documentId;
  }

  // If id is already a string (likely a documentId), use it
  if (typeof id === 'string') {
    console.log('✅ [Admin Resources] Using id as documentId (string):', id);
    return id;
  }

  // Otherwise, log warning and convert numeric id to string
  // Note: This might fail if the API expects documentId
  console.warn('⚠️ [Admin Resources] No documentId found, converting numeric id to string:', id);
  return id.toString();
};

/**
 * Helper to convert ContentManagerQueryOptions to match existing interfaces
 */
export const convertOptions = (options?: any): ContentManagerQueryOptions => {
  const cmOptions: ContentManagerQueryOptions = {};

  // Handle pagination. When the caller doesn't specify any, ask for the max
  // page size: the Content Manager API defaults to 10 records, which silently
  // hides data in dropdowns and lists that expect "all" records.
  if (options?.pagination) {
    cmOptions.page = options.pagination.page;
    cmOptions.pageSize = options.pagination.pageSize;
  } else {
    cmOptions.page = 1;
    cmOptions.pageSize = 100;
  }

  if (!options) return cmOptions;

  // Handle sort (strings, { field, order } objects, or arrays of either)
  const sortEntryToString = (entry: any): string => {
    if (typeof entry === 'string') return entry;
    if (entry && typeof entry === 'object' && entry.field) {
      return `${entry.field}:${entry.order?.toUpperCase() || 'ASC'}`;
    }
    return '';
  };
  if (options.sort) {
    if (Array.isArray(options.sort)) {
      cmOptions.sort = options.sort.map(sortEntryToString).filter(Boolean).join(',');
    } else {
      cmOptions.sort = sortEntryToString(options.sort);
    }
  }

  // Handle filters
  if (options.filters) {
    cmOptions.filters = options.filters;
  }

  // Handle populate
  if (options.populate) {
    cmOptions.populate = options.populate;
  }

  // Handle full-text search
  if (options.q) {
    cmOptions.q = options.q;
  }

  return cmOptions;
};

/**
 * Helper to convert Content Manager response to match existing format
 */
export const convertResponse = (response: any, collectionType?: string) => {
  // Content Manager returns { results, pagination }
  // Each result has: id, documentId, and other fields
  console.log('🔄 [Admin Resources] Converting response:', response);

  let data = response.results || response;

  // Log first item to see structure
  if (Array.isArray(data) && data.length > 0) {
    console.log('📋 [Admin Resources] First item:', data[0]);
    console.log('🔑 [Admin Resources] Has documentId?', !!data[0]?.documentId);
  }

  // Special handling for debit notes: convert flat fields back to nested objects
  if (collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.DEBIT_NOTES) {
    if (Array.isArray(data)) {
      data = data.map(item => transformDebitNoteFromStrapi(item));
    } else if (data && typeof data === 'object') {
      data = transformDebitNoteFromStrapi(data);
    }
  }

  // Reverse flatten for crew evaluations
  if (collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.CREW_EVALUATIONS) {
    const unflatten = (item: any) => {
      const t = { ...item };
      if (t.evaluationPeriodFrom !== undefined || t.evaluationPeriodTo !== undefined) {
        t.evaluationPeriod = { fromDate: t.evaluationPeriodFrom, toDate: t.evaluationPeriodTo };
        delete t.evaluationPeriodFrom;
        delete t.evaluationPeriodTo;
      }
      return t;
    };
    data = Array.isArray(data) ? data.map(unflatten) : unflatten(data);
  }

  // Reverse flatten for payment orders
  if (collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.PAYMENT_ORDERS) {
    const unflatten = (item: any) => {
      const t = { ...item };
      if (t.authorizedByName !== undefined || t.authorizedByJobTitle !== undefined) {
        t.authorizedBy = { signature: t.authorizedBySignature || '', name: t.authorizedByName || '', jobTitle: t.authorizedByJobTitle || '' };
        delete t.authorizedBySignature;
        delete t.authorizedByName;
        delete t.authorizedByJobTitle;
      }
      return t;
    };
    data = Array.isArray(data) ? data.map(unflatten) : unflatten(data);
  }

  // Reverse flatten for invoice hires
  if (collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.INVOICE_HIRES) {
    const unflatten = (item: any) => {
      const t = { ...item };
      if (t.financeManagerName !== undefined || t.financeManagerSignature !== undefined) {
        t.financeManager = { name: t.financeManagerName || '', signature: t.financeManagerSignature || '' };
        delete t.financeManagerName;
        delete t.financeManagerSignature;
      }
      if (t.generalManagerName !== undefined || t.generalManagerSignature !== undefined) {
        t.generalManager = { name: t.generalManagerName || '', signature: t.generalManagerSignature || '' };
        delete t.generalManagerName;
        delete t.generalManagerSignature;
      }
      if (t.insuranceStatus !== undefined) {
        t.socialInsurance = {
          insuranceStatus: t.insuranceStatus,
          clientDeliveryDate: t.clientDeliveryDate,
          insuranceDeliveryDate: t.insuranceDeliveryDate,
          certificateNumber: t.certificateNumber,
          certificateValue: t.certificateValue,
          clientReceiptDate: t.clientReceiptDate,
        };
        delete t.insuranceStatus;
        delete t.clientDeliveryDate;
        delete t.insuranceDeliveryDate;
        delete t.certificateNumber;
        delete t.certificateValue;
        delete t.clientReceiptDate;
      }
      return t;
    };
    data = Array.isArray(data) ? data.map(unflatten) : unflatten(data);
  }

  // Reverse flatten for delivery notes (signature blocks stored flat)
  if (collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.DELIVERY_NOTES) {
    const unflatten = (item: any) => {
      const t = { ...item };
      if (t.deliveredByName !== undefined || t.deliveredBySignature !== undefined) {
        t.deliveredBy = { name: t.deliveredByName || '', signature: t.deliveredBySignature || '' };
        delete t.deliveredByName;
        delete t.deliveredBySignature;
      }
      if (t.deliveredToName !== undefined || t.deliveredToSignature !== undefined || t.deliveredToZoom !== undefined) {
        t.deliveredTo = { name: t.deliveredToName || '', signature: t.deliveredToSignature || '', zoom: t.deliveredToZoom || '' };
        delete t.deliveredToName;
        delete t.deliveredToSignature;
        delete t.deliveredToZoom;
      }
      return t;
    };
    data = Array.isArray(data) ? data.map(unflatten) : unflatten(data);
  }

  // Reverse flatten for finance reports
  if (collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.FINANCE_REPORTS) {
    const unflatten = (item: any) => {
      const t = { ...item };
      if (t.dateRangeFrom !== undefined || t.dateRangeTo !== undefined) {
        t.dateRange = { from: t.dateRangeFrom, to: t.dateRangeTo };
        delete t.dateRangeFrom;
        delete t.dateRangeTo;
      }
      return t;
    };
    data = Array.isArray(data) ? data.map(unflatten) : unflatten(data);
  }

  // Reverse flatten for permit letters
  if (collectionType === API_CONFIG.CONTENT_MANAGER_ENDPOINTS.PERMIT_LETTERS) {
    const unflatten = (item: any) => {
      const t = { ...item };
      if (t.letterCreatedBy !== undefined) {
        t.createdBy = t.letterCreatedBy;
        delete t.letterCreatedBy;
      }
      return t;
    };
    data = Array.isArray(data) ? data.map(unflatten) : unflatten(data);
  }

  // Map populated relation objects to the *Id fields the frontend expects
  // (e.g. { client: { documentId } } -> clientId). Keeps the original object too.
  const RELATION_ID_FIELDS: Record<string, string> = {
    client: 'clientId', customer: 'customerId', vessel: 'vesselId', foa: 'foaId',
    provider: 'providerId', branch: 'branchId', bank: 'bankId', safe: 'safeId',
    invoice: 'invoiceId', invoiceHire: 'invoiceHireId', category: 'categoryId',
    contract: 'contractId', bill: 'billId', employee: 'employeeId',
    requisition: 'requisitionId',
  };
  const mapRelationIds = (item: any) => {
    if (!item || typeof item !== 'object') return item;
    const t = { ...item };
    Object.entries(RELATION_ID_FIELDS).forEach(([rel, idField]) => {
      const v = t[rel];
      if (v && typeof v === 'object' && !Array.isArray(v) && (v.documentId || v.id)) {
        if (t[idField] === undefined || t[idField] === null || t[idField] === '') {
          t[idField] = v.documentId || String(v.id);
        }
      }
    });
    return t;
  };
  data = Array.isArray(data) ? data.map(mapRelationIds) : mapRelationIds(data);

  // Apply reverse field name transformations (Strapi -> frontend)
  if (Array.isArray(data)) {
    data = data.map(item => reverseTransformFieldNames(item, collectionType));
  } else if (data && typeof data === 'object') {
    data = reverseTransformFieldNames(data, collectionType);
  }

  return {
    data: data,
    meta: response.pagination ? { pagination: response.pagination } : undefined,
  };
};
